<template>
    <section class="img-bg-bottom-text-section">
        <div class="img-bg-bottom-text-section__container">
            <CardTitle v-if="!hideTitle && item.mainTitle" :module="item" />

            <ScrollRow>
                <article v-for="(cell, idx) in item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="img-bg-bottom-text__card"
                    @click="handleCellClick(cell)">
                    <!-- Background image -->
                    <div v-if="getCellImage(cell)" class="img-bg-bottom-text__bg">
                        <img :src="getCellImage(cell)" :alt="getCellTitle(cell)" loading="lazy" />
                    </div>
                    <!-- Bottom gradient overlay for text readability -->
                    <div class="img-bg-bottom-text__overlay" />

                    <!-- Bottom text -->
                    <div v-if="getCellTitle(cell)" class="img-bg-bottom-text__text">
                        {{ getCellTitle(cell) }}
                    </div>
                </article>
            </ScrollRow>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}

function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}

function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.img-bg-bottom-text-section {
    width: 100%;
}

.img-bg-bottom-text-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
}


.img-bg-bottom-text__card {
    position: relative;
    width: 100%;
    height: fit-content;
    border-radius: 1rem;
    // overflow: hidden;    
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    background: #f0f0f3;

    @media (min-width: $breakpoint-md) {
        border-radius: 1.5rem;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);

        .img-bg-bottom-text__bg img {
            transform: scale(1.05);
        }
    }
}

.img-bg-bottom-text__bg {
    inset: 0;
    z-index: 0;

    img {
        width: 100%;
        height: auto;
        object-fit: cover;
        display: block;
        transition: transform 0.5s ease;
    }
}

.img-bg-bottom-text__overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    // height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0) 100%);
    pointer-events: none;
}

.img-bg-bottom-text__text {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    padding: 1rem 1.25rem;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.4;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    @media (min-width: $breakpoint-md) {
        padding: 1.5rem 1.75rem;
        font-size: 1.125rem;
    }
}
</style>
